import { API_BASE } from './config';
import type {
  NameSuggestions, SignupResult, SigninResult,
} from './sync-types';

// ─── Error types ──────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: Record<string, unknown>,
  ) { super(`${status} ${code}`); this.name = 'ApiError'; }
}

export class NotAuthenticatedError extends ApiError {
  constructor(body: Record<string, unknown>) { super(401, String(body.error ?? 'unauthorized'), body); this.name = 'NotAuthenticatedError'; }
}

export class ConflictError extends ApiError {
  public serverVersion: number;
  public serverUpdatedAt: number;
  constructor(body: Record<string, unknown>) {
    super(409, 'conflict', body);
    this.name = 'ConflictError';
    this.serverVersion = typeof body.serverVersion === 'number' ? body.serverVersion : 0;
    this.serverUpdatedAt = typeof body.serverUpdatedAt === 'number' ? body.serverUpdatedAt : 0;
  }
}

export class AmbiguousAuthError extends ApiError {
  constructor(body: Record<string, unknown>) {
    super(409, 'ambiguous', body);
    this.name = 'AmbiguousAuthError';
  }
}

export class RateLimitedError extends ApiError {
  public retryAfterSec?: number;
  constructor(body: Record<string, unknown>, retryAfter?: string | null) {
    super(429, String(body.error ?? 'rate_limited'), body);
    this.name = 'RateLimitedError';
    if (retryAfter) {
      const n = parseInt(retryAfter, 10);
      if (Number.isFinite(n)) this.retryAfterSec = n;
    }
  }
}

export class LimitExceededError extends ApiError {
  public field?: string;
  public limit?: number;
  public actual?: number;
  constructor(body: Record<string, unknown>) {
    super(422, String(body.error ?? 'limit_exceeded'), body);
    this.name = 'LimitExceededError';
    if (typeof body.field === 'string') this.field = body.field;
    if (typeof body.limit === 'number') this.limit = body.limit;
    if (typeof body.actual === 'number') this.actual = body.actual;
  }
}

// ─── Request helper ───────────────────────────────────────────

interface RequestOpts {
  body?: unknown;
  auth?: string;  // Bearer token
}

async function request<T>(method: string, path: string, opts: RequestOpts = {}): Promise<T> {
  if (!API_BASE) {
    throw new Error('VITE_API_URL is not set — check .env or Cloudflare Pages build env');
  }
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.auth) headers['Authorization'] = `Bearer ${opts.auth}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  let body: Record<string, unknown> = {};
  try { body = await res.json() as Record<string, unknown>; } catch { /* empty body */ }

  if (res.ok) return body as T;

  if (res.status === 401) throw new NotAuthenticatedError(body);
  if (res.status === 409) {
    if (body.error === 'ambiguous') throw new AmbiguousAuthError(body);
    throw new ConflictError(body);
  }
  if (res.status === 429) throw new RateLimitedError(body, res.headers.get('retry-after'));
  if (res.status === 422 || res.status === 413) throw new LimitExceededError(body);

  throw new ApiError(res.status, String(body.error ?? 'error'), body);
}

// ─── Auth endpoints ───────────────────────────────────────────

export function getSuggestions(count: number): Promise<NameSuggestions> {
  return request<NameSuggestions>('GET', `/auth/name-suggestions?count=${count}`);
}

export function signup(body: { displayName: string; nonce: string }):
  Promise<SignupResult & { sessionToken: string }> {
  return request('POST', '/auth/signup', { body });
}

export function login(body: { displayName: string; pin: string; discriminator?: string }):
  Promise<SigninResult & { sessionToken: string }> {
  return request('POST', '/auth/login', { body });
}

export function recover(body: { displayName: string; recoveryCode: string; discriminator?: string }):
  Promise<SignupResult & { sessionToken: string }> {
  return request('POST', '/auth/recover', { body });
}

export function rotatePin(auth: string): Promise<{ pin: string }> {
  return request('POST', '/auth/rotate-pin', { auth });
}

export function rotateRecovery(auth: string, body: { pin: string }): Promise<{ recoveryCode: string }> {
  return request('POST', '/auth/rotate-recovery', { auth, body });
}

export function logout(auth: string): Promise<void> {
  return request<void>('POST', '/auth/logout', { auth });
}

// ─── Level endpoints — declared here; tests in Task 6 ────────

export function pushLevel(auth: string, body: { level: unknown; isPublic?: boolean; expectedRemoteVersion?: number | null }):
  Promise<{ remoteId: string; version: number; slug: string }> {
  return request('POST', '/levels', { auth, body });
}

export function pullLevel(auth: string, remoteId: string):
  Promise<{
    level: import('@/game/level').LevelData;
    version: number;
    ownerDisplayName: string;
    isPublic: boolean;
    verifier: import('./sync-types').Verifier | null;
    currentVersionCompletionCount: number;
  }> {
  return request('GET', `/levels/${encodeURIComponent(remoteId)}`, { auth });
}

export function syncStatuses(auth: string, body: { levels: Array<{ remoteId: string; remoteVersion: number }> }):
  Promise<{ statuses: Record<string, {
    serverVersion: number; deleted: boolean; isPublic: boolean;
    verifier: import('./sync-types').Verifier | null;
    currentVersionCompletionCount: number;
  }> }> {
  return request('POST', '/levels/sync-status', { auth, body });
}

export function minelevels(auth: string):
  Promise<{ items: Array<{
    remoteId: string; slug: string; name: string; difficulty: string; modeFlags: number;
    version: number; sizeBytes: number; downloadCount: number; remixOf: string | null;
    updatedAt: number; isPublic: boolean;
  }> }> {
  return request('GET', '/levels/mine', { auth });
}

export function patchVisibility(auth: string, remoteId: string, body: { isPublic: boolean }):
  Promise<{ ok: boolean; isPublic: boolean }> {
  return request('PATCH', `/levels/${encodeURIComponent(remoteId)}/visibility`, { auth, body });
}

export function deleteLevel(auth: string, remoteId: string): Promise<void> {
  return request<void>('DELETE', `/levels/${encodeURIComponent(remoteId)}`, { auth });
}

export function browse(auth: string, params: Record<string, string | string[]>):
  Promise<{ items: unknown[]; nextCursor: string | null }> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (Array.isArray(v)) for (const vv of v) qs.append(k, vv);
    else qs.append(k, v);
  }
  return request('GET', `/browse?${qs.toString()}`, { auth });
}

export function remixLevel(auth: string, remoteId: string):
  Promise<{ level: unknown; version: number; remoteId: string; ownerDisplayName: string; isPublic: boolean }> {
  return request('POST', `/levels/${encodeURIComponent(remoteId)}/remix`, { auth });
}

export function submitCompletion(
  auth: string, remoteId: string, body: { levelVersion: number },
): Promise<{ count: number; isVerifier: boolean; firstCompletedAt: number }> {
  return request('POST', `/levels/${encodeURIComponent(remoteId)}/complete`, { auth, body });
}

export function listCompletions(
  auth: string, remoteId: string, opts: { cursor?: string; limit?: number } = {},
): Promise<{
  items: Array<{
    userId: string; displayName: string; firstCompletedAt: number; count: number; isVerifier: boolean;
  }>;
  nextCursor: string | null;
}> {
  const qs = new URLSearchParams();
  if (opts.cursor) qs.set('cursor', opts.cursor);
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  return request('GET', `/levels/${encodeURIComponent(remoteId)}/completions?${qs.toString()}`, { auth });
}
